import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AdminRoute from './components/AdminRoute';
import ContractorRoute from './components/ContractorRoute';
import Login from './pages/Login';
import SetPassword from './pages/SetPassword';
import Dashboard from './pages/Dashboard';
import Contractors from './pages/Contractors';
import ContractorDetails from './components/contractors/ContractorDetails';
import Projects from './pages/Projects';
import TimeEntries from './pages/TimeEntries';
import Invoices from './pages/Invoices';
import Payments from './pages/Payments';
import ContractorDashboard from './pages/ContractorDashboard';
import ContractorTimeEntries from './pages/ContractorTimeEntries';
import ContractorTimeEntryForm from './pages/ContractorTimeEntryForm';
import ContractorInvoices from './pages/ContractorInvoices';
import ContractorInvoiceDetail from './pages/ContractorInvoiceDetail';
import ContractorProfile from './pages/ContractorProfile';
import Layout from './components/Layout';
import ContractorLayout from './components/ContractorLayout';
import { ContractorProvider } from './contexts/ContractorContext';

// Administrator Orange Theme
const adminTheme = createTheme({
  palette: {
    primary: {
      main: '#FF6B35', // Vibrant orange
      light: '#FF8A65',
      dark: '#E64A19',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#F97316', // Alternative orange
      light: '#FFB74D',
      dark: '#E65100',
      contrastText: '#ffffff',
    },
    background: {
      default: '#FFF5F0',
      paper: '#ffffff',
    },
    error: {
      main: '#D32F2F',
    },
    warning: {
      main: '#F57C00',
    },
    info: {
      main: '#1976d2',
    },
    success: {
      main: '#388E3C',
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FF6B35',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: '#FF6B35',
          '&:hover': {
            backgroundColor: '#E64A19',
          },
        },
      },
    },
  },
});

// Contractor Blue Theme (existing)
const contractorTheme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#dc004e',
      light: '#ff5983',
      dark: '#9a0036',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
});

// Neutral theme for login page (supports both admin and contractor)
const loginTheme = createTheme({
  palette: {
    primary: {
      main: '#666',
      light: '#999',
      dark: '#333',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
});

// Global route guard to prevent contractors from accessing admin routes
// This component must be inside Router and AuthProvider
const RouteGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Hooks must be called unconditionally - always call useAuth
  const { user, loading } = useAuth();

  // Use useEffect unconditionally - hooks must be in same order every render
  React.useEffect(() => {
    console.log('[RouteGuard] ====== ROUTE GUARD CHECK ======');
    console.log('[RouteGuard] Path:', location.pathname);
    console.log('[RouteGuard] User:', user?.email);
    console.log('[RouteGuard] Role:', user?.role);
    console.log('[RouteGuard] Loading:', loading);
    
    // DON'T redirect if still loading - wait for auth to complete
    if (loading) {
      console.log('[RouteGuard] Still loading, waiting...');
      return;
    }
    
    if (!user) {
      console.log('[RouteGuard] No user, will be handled by PrivateRoute');
      return;
    }
    
    const userRole = user.role?.toUpperCase();
    const isContractorRoute = location.pathname.startsWith('/contractor');
    
    // Valid admin routes that should NOT be redirected
    const validAdminRoutes = ['/dashboard', '/contractors', '/projects', '/time-entries', '/invoices', '/payments'];
    
    // CRITICAL: Allow /contractors for ADMIN users FIRST - before any other checks
    if (userRole === 'ADMIN' && location.pathname === '/contractors') {
      console.log('[RouteGuard] ✅✅✅ /contractors route EXPLICITLY ALLOWED for ADMIN - NO REDIRECT');
      return;
    }
    
    const isAdminRoute = !isContractorRoute && 
                        location.pathname !== '/login' && 
                        location.pathname !== '/set-password' &&
                        location.pathname !== '/';

    // CRITICAL SECURITY: Block contractors from accessing ANY admin routes
    if (userRole === 'CONTRACTOR' && isAdminRoute) {
      console.warn('[RouteGuard] SECURITY: Contractor attempting to access admin route, redirecting:', location.pathname);
      navigate('/contractor/dashboard', { replace: true });
      return;
    }
    
    // CRITICAL SECURITY: Block admins from accessing ANY contractor routes
    if (userRole === 'ADMIN' && isContractorRoute) {
      console.warn('[RouteGuard] SECURITY: Admin attempting to access contractor route, redirecting:', location.pathname);
      navigate('/dashboard', { replace: true });
      return;
    }
    
    // DON'T redirect from valid admin routes - allow them to render
    if (userRole === 'ADMIN' && validAdminRoutes.includes(location.pathname)) {
      console.log('[RouteGuard] ✅ Valid admin route, allowing access to:', location.pathname);
      return;
    }
    
    // Only redirect contractors from root
    if (userRole === 'CONTRACTOR' && location.pathname === '/') {
      console.warn('[RouteGuard] Contractor on root route, redirecting to contractor dashboard');
      navigate('/contractor/dashboard', { replace: true });
      return;
    }
    
    console.log('[RouteGuard] Route is valid, allowing access to:', location.pathname);
  }, [location.pathname, user, loading, navigate]);

  return <>{children}</>;
};

// Theme wrapper component to apply theme based on route
const ThemeWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isContractorRoute = location.pathname.startsWith('/contractor');
  const isLoginRoute = location.pathname === '/login' || location.pathname.startsWith('/set-password');
  
  // Use neutral theme for login page, orange for admin routes, blue for contractor routes
  const theme = isLoginRoute ? loginTheme : (isContractorRoute ? contractorTheme : adminTheme);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <RouteGuard>
          <ThemeWrapper>
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route
              path="/contractor"
              element={
                <ContractorRoute>
                  <ContractorProvider>
                    <ContractorLayout />
                  </ContractorProvider>
                </ContractorRoute>
              }
            >
              <Route index element={<Navigate to="/contractor/dashboard" replace />} />
              <Route path="dashboard" element={<ContractorDashboard />} />
              <Route path="time-entries" element={<ContractorTimeEntries />} />
              <Route path="time-entries/new" element={<ContractorTimeEntryForm />} />
              <Route path="invoices" element={<ContractorInvoices />} />
              <Route path="invoices/:id" element={<ContractorInvoiceDetail />} />
              <Route path="profile" element={<ContractorProfile />} />
            </Route>
            {/* Admin routes - only accessible to ADMIN role */}
            <Route
              path="/"
              element={
                <AdminRoute>
                  <Layout />
                </AdminRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="contractors" element={<Contractors />} />
              <Route path="contractors/:contractorId" element={<ContractorDetails />} />
              <Route path="projects" element={<Projects />} />
              <Route path="time-entries" element={<TimeEntries />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="payments" element={<Payments />} />
              {/* Catch-all for unmatched admin routes - redirect to dashboard */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Routes>
          </ThemeWrapper>
        </RouteGuard>
      </Router>
    </AuthProvider>
  );
}

export default App;

